import { Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { Button } from "../../../components";
import useApp from "../../../hooks/useApp";
import { DetailsModal, NgoDetailsModal, ThanksModal } from "../../../modals";
import { IUserInputs } from "../../../modals/details-modal/const";
import { giveDonation } from "../../../services/Donation.service";
import { getNGOSByCity } from "../../../services/NGO.service";
import { giveVote } from "../../../services/Vote.service";
import { INGO } from "../../../types";
import { getNumber } from "../../../utils";
import { showError } from "../../../utils/error";
import indianNumberFormatter from "../../../utils/indian-number-formatter";
import NGOCard from "./components/NgoCard";
import styles from "./ngo-listing.module.css";

const NgoListing = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const organizationId = searchParams.get("organization");
  const cityId = searchParams.get("city");
  const all = searchParams.get("all");
  const {
    state: { organization, isVoting },
  } = useApp();
  const [open, setOpen] = useState(false);
  const [selectedNGOS, setSelectedNGOS] = useState<
    {
      _id: string;
      value: string;
      projects: string[];
    }[]
  >([]);
  const [openThanksModal, setOpenThanksModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [ngos, setNGOS] = useState<INGO[]>([]);
  const [selectedNGO, setSelectedNGO] = useState<INGO | null>(null);
  const [errors, setErrors] = useState<any>({});
  const [finalSelectedNGOS, setFinalSelectedNGOS] = useState<
    { ngoId: string; amount: number }[]
  >([]);

  // Loaders
  const [isVotingLoader, setIsVotingLoader] = useState(false);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, _id: string) => {
      const { checked } = event?.target;

      if (
        organization?.isVoting &&
        selectedNGOS.length >= (organization?.votingLimit ?? 0) &&
        checked
      ) {
        return toast.error(
          `You can vote only ${organization?.votingLimit} ngos`
        );
      }

      if (checked) {
        setSelectedNGOS((prev) => [...prev, { _id, value: "", projects: [] }]);
      } else {
        setSelectedNGOS((prev) => prev.filter((ngo) => ngo?._id !== _id));

        setErrors((prev: any) => {
          const newErrors = { ...prev };
          delete newErrors[_id];
          return newErrors;
        });
      }
    },
    [organization, selectedNGOS]
  );

  const handleProjectToggle = useCallback(
    (ngoId: string, projectId: string, checked: boolean) => {
      setSelectedNGOS((prev) =>
        prev.map((ngo) =>
          ngo._id === ngoId
            ? {
                ...ngo,
                projects: checked
                  ? [...(ngo.projects || []), projectId]
                  : (ngo.projects || []).filter((id) => id !== projectId),
              }
            : ngo
        )
      );
    },
    []
  );

  const handleChangeValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, _id: string) => {
      const { value } = event?.target;
      const formattedValue = indianNumberFormatter(String(value));
      if (formattedValue === null) return;

      const numValue = getNumber(formattedValue);

      setSelectedNGOS((prev) =>
        prev.map((ngo) =>
          ngo._id === _id ? { ...ngo, value: formattedValue } : ngo
        )
      );

      if (numValue <= 0) {
        setErrors((prev: any) => ({
          ...prev,
          [_id]: "Value must be more than 0",
        }));
      } else {
        setErrors((prev: any) => {
          const newErrors = { ...prev };
          delete newErrors[_id];
          return newErrors;
        });
      }
    },
    [selectedNGOS]
  );

  const fetchNGOS = useCallback(async () => {
    try {
      const res = await getNGOSByCity({
        organizationId: organizationId ?? "null",
        cityId: cityId ?? "null",
        all: all ?? "no",
      });
      setNGOS(res?.data || []);
    } catch (error) {
      showError(error);
    }
  }, [organizationId, cityId, all]);

  useEffect(() => {
    fetchNGOS();
  }, [fetchNGOS]);

  const handleVote = useCallback(
    async (values: IUserInputs) => {
      setIsVotingLoader(true);
      try {
        let res = null;
        if (isVoting) {
          res = await giveVote({
            ...values,
            organization: organizationId as string,
            ngos: selectedNGOS.map(({ _id }) => _id),
          });
        }
        toast.success(res?.message || "Vote submitted successfully!");
        setOpenDetailsModal(false);
        setOpenThanksModal(true);
        setSelectedNGOS([]);
      } catch (error) {
        showError(error);
      } finally {
        setIsVotingLoader(false);
      }
    },
    [organizationId, selectedNGOS, isVoting]
  );

  const handleSubmit = useCallback(() => {
    if (!isVoting) {
      const newErrors = { ...errors };
      selectedNGOS.forEach(({ _id, value }) => {
        if (getNumber(value) <= 0) {
          newErrors[_id] = "Value must be more than 0";
        }
      });
      if (Object.keys(newErrors)?.length) {
        toast.error("Please enter valid amount for selected NGOs");
        return setErrors(newErrors);
      }

      const formattedNGOS = selectedNGOS.map((item) => ({
        ngoId: item._id,
        amount: getNumber(item.value),
        projects: item.projects || [],
      }));

      setFinalSelectedNGOS(formattedNGOS);
    }
    setOpenDetailsModal(true);
  }, [errors, selectedNGOS, isVoting]);

  const handleDonation = useCallback(
    async (values: IUserInputs) => {
      try {
        const payload = {
          ...values,
          donations: finalSelectedNGOS,
          organization: organizationId as string,
        };
        const res = await giveDonation(payload);
        toast.success(res?.message);
        setOpenDetailsModal(false);
        setOpenThanksModal(true);
        setSelectedNGOS([]);
        setFinalSelectedNGOS([]);
        await fetchNGOS();
      } catch (error) {
        showError(error);
      }
    },
    [organizationId, finalSelectedNGOS, fetchNGOS]
  );

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.headingContainer}>
          {<h2>BLR SETU 2025</h2>}
          <h2>Fundraising event</h2>
        </div>
        <div className={styles.descriptions}>
          <p>{organization?.title}</p>
          <p>{organization?.description}</p>
        </div>
      </div>
      <div className={styles.content}>
        {isVoting ? (
          <h2>
            <p className={styles.primaryHeader}>
              Voting is going on, Please add your valuable vote
            </p>
          </h2>
        ) : (
          <p className={styles.primaryHeader}>Banglore SETU 2025 Fundraising</p>
        )}
        <p className={styles.secondaryHeader}>
          Select the NGO's you want to {isVoting ? "vote" : "donate"}
        </p>
        {ngos.length ? (
          <div className={styles.cardWrapper}>
            {ngos.map((ngo) => {
              return (
                <NGOCard
                  key={ngo?._id}
                  ngo={ngo}
                  selectedNGOS={selectedNGOS}
                  onCheckboxChange={handleCheckboxChange}
                  onViewMoreClick={() => {
                    setOpen(true);
                    setSelectedNGO(ngo);
                  }}
                  onValueChange={handleChangeValue}
                  isVoting={isVoting}
                  errors={errors}
                  onProjectToggle={handleProjectToggle}
                />
              );
            })}
          </div>
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "18px",
              marginTop: "50px",
              backgroundColor: "#305ccd12",
              padding: "24px 0px",
              borderRadius: "14px",
            }}
          >
            No NGOs Found!
          </Typography>
        )}
      </div>
      {!!selectedNGOS.length && (
        <Button
          disabled={!selectedNGOS.length}
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleSubmit}
        >
          Continue
        </Button>
      )}
      <NgoDetailsModal
        open={open && !!selectedNGO}
        onClose={() => {
          setOpen(false);
          setSelectedNGO(null);
        }}
        selectedNGO={selectedNGO}
      />
      <DetailsModal
        open={openDetailsModal}
        onClose={() => {
          setOpenDetailsModal(false);
        }}
        onSubmit={(values) => {
          if (isVoting) {
            handleVote(values);
          } else {
            handleDonation(values);
          }
        }}
        isPanRequired={
          !!selectedNGOS.find(
            (ngo) => Number(ngo.value?.replaceAll(",", "")) > 50000
          )
        }
        loading={isVotingLoader}
      />
      <ThanksModal
        open={openThanksModal}
        onClose={() => setOpenThanksModal(false)}
      />
    </div>
  );
};

export default NgoListing;
